<script setup>
import { ref, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    mobils: Array
});

// State
const ulasans = ref([]);
const loading = ref(true);
const saving = ref(false);
const showModal = ref(false);
const isEditing = ref(false);

const form = ref({
    id: null,
    nama_pelanggan: '',
    mobil_id: '',
    rating: 5,
    ulasan: '',
    tanggal_sewa: '',
    is_active: true,
    foto: null
});

// Methods
const loadUlasans = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/ulasan/get');
        if (response.data.status === 'success') {
            ulasans.value = response.data.data;
        }
    } catch (error) {
        console.error('Error:', error);
    } finally {
        loading.value = false;
    }
};

const openModal = (ulasan = null) => {
    if (ulasan) {
        form.value = {
            id: ulasan.id,
            nama_pelanggan: ulasan.nama_pelanggan,
            mobil_id: ulasan.mobil_id || '',
            rating: ulasan.rating,
            ulasan: ulasan.ulasan,
            tanggal_sewa: ulasan.tanggal_sewa || '',
            is_active: ulasan.is_active,
            foto: null
        };
        isEditing.value = true;
    } else {
        form.value = {
            id: null,
            nama_pelanggan: '',
            mobil_id: '',
            rating: 5,
            ulasan: '',
            tanggal_sewa: '',
            is_active: true,
            foto: null
        };
        isEditing.value = false;
    }
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
};

const handleFileChange = (e) => {
    form.value.foto = e.target.files[0];
};

const submitForm = async () => {
    saving.value = true;
    
    const formData = new FormData();
    formData.append('nama_pelanggan', form.value.nama_pelanggan);
    formData.append('mobil_id', form.value.mobil_id);
    formData.append('rating', form.value.rating);
    formData.append('ulasan', form.value.ulasan);
    formData.append('tanggal_sewa', form.value.tanggal_sewa);
    formData.append('is_active', form.value.is_active ? 1 : 0);
    
    if (form.value.foto) {
        formData.append('foto', form.value.foto);
    }
    
    let url = '/admin/ulasan';
    if (isEditing.value) {
        formData.append('_method', 'PUT');
        url = `/admin/ulasan/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.status === 'success') {
            alert(response.data.message);
            closeModal();
            loadUlasans();
        }
    } catch (error) {
        if (error.response?.data?.messages) {
            alert(error.response.data.messages.join('\n'));
        } else {
            alert(error.response?.data?.message || 'Terjadi kesalahan');
        }
    } finally {
        saving.value = false;
    }
};

const deleteUlasan = async (id) => {
    if (!confirm('Hapus ulasan ini?')) return;
    
    try {
        const response = await axios.delete(`/admin/ulasan/${id}`);
        if (response.data.status === 'success') {
            alert(response.data.message);
            loadUlasans();
        }
    } catch (error) {
        alert('Gagal menghapus');
    }
};

const toggleStatus = async (ulasan) => {
    try {
        await axios.post('/admin/ulasan/toggle-status', {
            id: ulasan.id,
            is_active: !ulasan.is_active
        });
        loadUlasans();
    } catch (error) {
        alert('Gagal mengubah status');
    }
};

const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
};

onMounted(() => {
    loadUlasans();
});
</script>

<template>
    <Head title="Kelola Ulasan" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <h3 class="page-title">Kelola Ulasan</h3>
                    <nav>
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                            <li class="breadcrumb-item active">Ulasan</li>
                        </ol>
                    </nav>
                </div>
                <button class="btn btn-primary" @click="openModal()">
                    <i class="fa fa-plus me-5"></i> Tambah Ulasan
                </button>
            </div>
        </div>

        <section class="content">
            <div class="card">
                <div class="card-header">
                    <h4 class="card-title"><i class="ti-comments me-10"></i>Daftar Ulasan Pelanggan</h4>
                </div>
                <div class="card-body">
                    <div class="table-responsive">
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Pelanggan</th>
                                    <th>Mobil</th>
                                    <th>Rating</th>
                                    <th>Ulasan</th>
                                    <th>Status</th>
                                    <th>Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loading">
                                    <td colspan="7" class="text-center p-5">
                                        <div class="spinner-border text-primary"></div>
                                    </td>
                                </tr>
                                <tr v-else-if="ulasans.length === 0">
                                    <td colspan="7" class="text-center text-muted p-5">Belum ada ulasan</td>
                                </tr>
                                <tr v-else v-for="(ulasan, index) in ulasans" :key="ulasan.id">
                                    <td>{{ index + 1 }}</td>
                                    <td>
                                        <div class="d-flex align-items-center">
                                            <img v-if="ulasan.foto" :src="'/storage/ulasan/' + ulasan.foto" 
                                                class="rounded-circle me-2" style="width: 40px; height: 40px; object-fit: cover;">
                                            <div v-else class="bg-light rounded-circle me-2 d-flex align-items-center justify-content-center" 
                                                style="width: 40px; height: 40px;">
                                                <i class="fa fa-user"></i>
                                            </div>
                                            <strong>{{ ulasan.nama_pelanggan }}</strong>
                                        </div>
                                    </td>
                                    <td>{{ ulasan.mobil?.nama || '-' }}</td>
                                    <td class="text-warning">{{ renderStars(ulasan.rating) }}</td>
                                    <td>{{ ulasan.ulasan?.substring(0, 50) }}...</td>
                                    <td>
                                        <span :class="['badge', ulasan.is_active ? 'badge-success' : 'badge-secondary']">
                                            {{ ulasan.is_active ? 'Aktif' : 'Nonaktif' }}
                                        </span>
                                    </td>
                                    <td>
                                        <div class="btn-group btn-group-sm">
                                            <button class="btn btn-outline-primary" @click="openModal(ulasan)">
                                                <i class="fa fa-edit"></i>
                                            </button>
                                            <button class="btn btn-outline-warning" @click="toggleStatus(ulasan)">
                                                <i :class="ulasan.is_active ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                                            </button>
                                            <button class="btn btn-outline-danger" @click="deleteUlasan(ulasan.id)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{ isEditing ? 'Edit' : 'Tambah' }} Ulasan</h5>
                    <button type="button" class="btn-close" @click="closeModal"></button>
                </div>
                <form @submit.prevent="submitForm">
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group mb-3">
                                    <label class="form-label">Nama Pelanggan <span class="text-danger">*</span></label>
                                    <input type="text" v-model="form.nama_pelanggan" class="form-control" required>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label">Mobil</label>
                                    <select v-model="form.mobil_id" class="form-control">
                                        <option value="">Pilih Mobil</option>
                                        <option v-for="mobil in mobils" :key="mobil.id" :value="mobil.id">
                                            {{ mobil.nama }}
                                        </option>
                                    </select>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label">Rating <span class="text-danger">*</span></label>
                                    <select v-model="form.rating" class="form-control" required>
                                        <option v-for="r in 5" :key="r" :value="r">{{ r }} Bintang</option>
                                    </select>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group mb-3">
                                    <label class="form-label">Foto Pelanggan</label>
                                    <input type="file" class="form-control" @change="handleFileChange" accept="image/*">
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label">Tanggal Sewa</label>
                                    <input type="date" v-model="form.tanggal_sewa" class="form-control">
                                </div>
                                <div class="form-group">
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" v-model="form.is_active" id="is_active">
                                        <label class="form-check-label" for="is_active">Aktif</label>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12">
                                <div class="form-group mb-3">
                                    <label class="form-label">Ulasan <span class="text-danger">*</span></label>
                                    <textarea v-model="form.ulasan" class="form-control" rows="4" required></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeModal">Batal</button>
                        <button type="submit" class="btn btn-primary" :disabled="saving">
                            <i v-if="saving" class="fa fa-spinner fa-spin me-1"></i> Simpan
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
